import { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { AlertTriangle, Link2, Plus, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Modal } from '@/components/common/Modal'
import { PageSpinner } from '@/components/common/Spinner'
import { InstanceCard } from '@/components/instance/InstanceCard'
import { InstanceForm } from '@/components/instance/InstanceForm'
import { QRCodeDisplay } from '@/components/instance/QRCodeDisplay'
import { useAuth } from '@/hooks/useAuth'
import InstancesService from '@/services/instances.service'
import { useAgentCatalogStore } from '@/stores/agentCatalogStore'
import { useInstancesStore } from '@/stores/instancesStore'
import type { Instance } from '@/types'
import { serializeAgentDefinitionForRuntime } from '@/utils/agentRuntime'

export default function InstancesPage() {
  const {
    instances,
    selectedInstance,
    isLoading,
    fetchInstances,
    setSelected,
    addInstance,
    removeInstance,
    updateInstance,
    updateInstanceStatus,
  } = useInstancesStore()
  const { can } = useAuth()
  const canManageInstances = can('manage_instances')
  const agents = useAgentCatalogStore((state) => state.agents)
  const fetchAgents = useAgentCatalogStore((state) => state.fetchAgents)

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isQrOpen, setIsQrOpen] = useState(false)
  const [qrFlowMode, setQrFlowMode] = useState<'prepare' | 'status'>('prepare')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [instanceToDelete, setInstanceToDelete] = useState<{ id: string; name: string } | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [instanceWebhookDraft, setInstanceWebhookDraft] = useState<{
    id: string
    name: string
    webhook_url: string
    webhook_enabled: boolean
  } | null>(null)
  const [isSavingWebhook, setIsSavingWebhook] = useState(false)
  const hydratedProfileIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    fetchInstances().catch(() => undefined)
    fetchAgents().catch(() => undefined)
  }, [fetchInstances, fetchAgents])

  const orderedInstances = useMemo(
    () => [...instances].sort((left, right) => right.updated_at.localeCompare(left.updated_at)),
    [instances],
  )

  const connectedCount = useMemo(
    () => orderedInstances.filter((instance) => instance.status === 'connected').length,
    [orderedInstances],
  )

  const qrPendingCount = useMemo(
    () => orderedInstances.filter((instance) => instance.status === 'qr_ready').length,
    [orderedInstances],
  )

  useEffect(() => {
    const candidates = orderedInstances.filter(
      (instance) =>
        instance.status === 'connected' &&
        (!instance.profile_picture_url || !instance.phone_number) &&
        !hydratedProfileIds.current.has(instance.id),
    )

    if (candidates.length === 0) return

    let cancelled = false
    candidates.forEach((instance) => hydratedProfileIds.current.add(instance.id))

    Promise.all(
      candidates.map(async (instance) => {
        try {
          const result = await InstancesService.getStatus(instance.id)
          if (cancelled) return
          updateInstance(instance.id, {
            status: result.status,
            phone_number: result.phone_number ?? undefined,
            jid: result.jid ?? undefined,
            profile_picture_url: result.profile_picture_url ?? undefined,
            qr_code_data: result.qr_code_data,
            public_qr_url: result.public_qr_url,
            remote_instance_id: result.remote_instance_id,
            last_connection_at: result.connected_at ?? undefined,
          })
        } catch {
          hydratedProfileIds.current.delete(instance.id)
        }
      }),
    ).catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [orderedInstances, updateInstance])

  async function handleCreate(data: { name: string; webhook_url?: string }) {
    setIsSubmitting(true)
    try {
      const instance = await InstancesService.create({
        name: data.name,
        webhook_url: data.webhook_url,
      })
      const preparedInstance: Instance = {
        ...instance,
        status: instance.status === 'connected' ? 'connected' : 'connecting',
        qr_code_data: undefined,
      }
      addInstance(preparedInstance)
      setSelected(preparedInstance)
      setIsCreateOpen(false)
      setQrFlowMode('prepare')
      setIsQrOpen(true)
      toast.success(`Instância "${instance.name}" criada. Aguarde a preparação inicial do QR Code.`)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail
        toast.error(detail ?? 'Não foi possível criar a instância.')
      } else {
        toast.error('Não foi possível criar a instância.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleRefresh() {
    setIsRefreshing(true)
    try {
      hydratedProfileIds.current.clear()
      await fetchInstances()
      toast.success('Lista de instâncias atualizada.')
    } finally {
      setIsRefreshing(false)
    }
  }

  function openConnectionModal(instance: Instance) {
    setSelected(instance)
    setQrFlowMode(
      instance.status === 'connecting' || (instance.status === 'disconnected' && !instance.qr_code_data)
        ? 'prepare'
        : 'status',
    )
    setIsQrOpen(true)
  }

  async function handleBindAgent(instance: Instance, agentId: string | null) {
    const selectedAgent = agentId ? agents.find((agent) => agent.id === agentId) : null

    if (agentId && !selectedAgent) {
      toast.error('O fluxo selecionado não foi encontrado no catálogo local.')
      return
    }

    try {
      const updated = await InstancesService.updateSettings(instance.id, {
        settings: {
          ...(instance.settings ?? {}),
          ai_agent_binding: agentId ? { agent_id: agentId } : null,
          agent_runtime_profile: selectedAgent ? serializeAgentDefinitionForRuntime(selectedAgent) : null,
        },
      })
      updateInstance(instance.id, updated)
      toast.success(agentId ? 'Fluxo vinculado à instância.' : 'Vínculo do fluxo removido.')
    } catch {
      toast.error('Não foi possível atualizar o vínculo do fluxo.')
    }
  }

  async function handleDisconnect(instanceId: string) {
    try {
      await InstancesService.disconnect(instanceId)
      updateInstanceStatus(instanceId, 'disconnected')
      toast.success('Instância desconectada com sucesso.')
    } catch {
      toast.error('Não foi possível desconectar a instância.')
    }
  }

  async function handleDeleteConfirmed() {
    if (!instanceToDelete) return
    setIsDeleting(true)
    try {
      await InstancesService.delete(instanceToDelete.id)
      removeInstance(instanceToDelete.id)
      toast.success(`Instância "${instanceToDelete.name}" removida.`)
      setInstanceToDelete(null)
    } catch {
      toast.error('Não foi possível remover a instância.')
    } finally {
      setIsDeleting(false)
    }
  }

  async function handleTogglePublicLink(instance: Instance, enabled: boolean) {
    try {
      const updated = await InstancesService.updateSettings(instance.id, {
        public_link_enabled: enabled,
      })
      updateInstance(instance.id, updated)
      toast.success(
        enabled
          ? 'Link público ativado para a instância.'
          : 'Link público desativado para a instância.',
      )
    } catch {
      toast.error('Não foi possível atualizar o link público.')
    }
  }

  async function handleCopyPublicLink(instance: Instance) {
    if (!instance.public_qr_url) {
      toast.error('Ative o link público antes de copiar.')
      return
    }

    try {
      await navigator.clipboard.writeText(instance.public_qr_url)
      toast.success('Link público copiado.')
    } catch {
      toast.error('Não foi possível copiar o link público.')
    }
  }

  async function handleSaveInstanceWebhook() {
    if (!instanceWebhookDraft) return
    if (instanceWebhookDraft.webhook_enabled && !instanceWebhookDraft.webhook_url.trim()) {
      toast.error('Informe a URL do webhook antes de ativar o envio.')
      return
    }

    setIsSavingWebhook(true)
    try {
      const updated = await InstancesService.updateSettings(instanceWebhookDraft.id, {
        webhook_url: instanceWebhookDraft.webhook_url.trim() || undefined,
        webhook_enabled: instanceWebhookDraft.webhook_enabled,
      })
      updateInstance(instanceWebhookDraft.id, updated)
      setInstanceWebhookDraft(null)
      toast.success('Webhook da instância atualizado.')
    } catch {
      toast.error('Não foi possível salvar o webhook da instância.')
    } finally {
      setIsSavingWebhook(false)
    }
  }

  if (isLoading && instances.length === 0) {
    return <PageSpinner label="Carregando instâncias..." />
  }

  return (
    <div className="space-y-8">
      <section className="glass-panel relative overflow-hidden p-6 sm:p-7">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(var(--color-brand-rgb),0.14),transparent_42%)]" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl space-y-3">
            <p className="text-xs uppercase tracking-[0.28em] text-[var(--text-muted)]">
              Conexões WhatsApp
            </p>
            <h2 className="text-3xl font-semibold tracking-[-0.03em] text-[var(--text-primary)] sm:text-4xl">
              Gerencie o ciclo de vida das suas sessões
            </h2>
            <p className="max-w-xl text-sm text-[var(--text-secondary)] sm:text-base">
              Crie, conecte e acompanhe cada sessão com QR Code, status em tempo real e link público opcional.
            </p>
          </div>

          <div className="relative flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              icon={<RefreshCw size={14} />}
              onClick={handleRefresh}
              loading={isRefreshing}
            >
              Atualizar
            </Button>
            {canManageInstances && (
              <Button icon={<Plus size={14} />} onClick={() => setIsCreateOpen(true)}>
                Nova instância
              </Button>
            )}
          </div>
        </div>

        <div className="relative mt-6 grid gap-3 md:grid-cols-3">
          <div className="rounded-[20px] border border-[var(--border-subtle)] bg-[var(--glass-light)] p-4">
            <p className="text-sm text-[var(--text-muted)]">Total de instâncias</p>
            <p className="mt-2 text-3xl font-semibold text-[var(--text-primary)]">
              {orderedInstances.length}
            </p>
          </div>
          <div className="rounded-[20px] border border-[rgba(16,185,129,0.18)] bg-[rgba(16,185,129,0.08)] p-4">
            <p className="text-sm text-[var(--text-muted)]">Conectadas</p>
            <p className="mt-2 text-3xl font-semibold text-[var(--color-success)]">
              {connectedCount}
            </p>
          </div>
          <div className="rounded-[20px] border border-[rgba(59,130,246,0.18)] bg-[rgba(59,130,246,0.08)] p-4">
            <p className="text-sm text-[var(--text-muted)]">Aguardando QR</p>
            <p className="mt-2 text-3xl font-semibold text-[var(--color-info)]">
              {qrPendingCount}
            </p>
          </div>
        </div>
      </section>

      {orderedInstances.length === 0 ? (
        <div className="glass-card p-8">
          <h2 className="text-xl font-semibold text-[var(--text-primary)]">
            Nenhuma instância cadastrada
          </h2>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Crie sua primeira instância para iniciar o pareamento com o WhatsApp e liberar o link público de QR.
          </p>
          {canManageInstances && (
            <div className="mt-5">
              <Button icon={<Plus size={14} />} onClick={() => setIsCreateOpen(true)}>
                Nova instância
              </Button>
            </div>
          )}
        </div>
      ) : (
        <section className="grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
          {orderedInstances.map((instance) => (
            <InstanceCard
              key={instance.id}
              instance={instance}
              isAdmin={canManageInstances}
              onViewQR={() => openConnectionModal(instance)}
              onSettings={() => {
                setSelected(instance)
                setInstanceWebhookDraft({
                  id: instance.id,
                  name: instance.name,
                  webhook_url: instance.webhook_url ?? '',
                  webhook_enabled: instance.webhook_enabled,
                })
              }}
              onDisconnect={() => handleDisconnect(instance.id)}
              onDelete={() => setInstanceToDelete({ id: instance.id, name: instance.name })}
              onTogglePublicLink={(enabled) => handleTogglePublicLink(instance, enabled)}
              onCopyPublicLink={() => handleCopyPublicLink(instance)}
              onBindAgent={(agentId) => handleBindAgent(instance, agentId)}
            />
          ))}
        </section>
      )}

      <Modal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Criar instância"
        description="Provisiona a conexão e já prepara o QR Code para o pareamento."
      >
        <InstanceForm
          onSubmit={handleCreate}
          onCancel={() => setIsCreateOpen(false)}
          isLoading={isSubmitting}
        />
      </Modal>

      <Modal
        open={isQrOpen}
        onClose={() => setIsQrOpen(false)}
        title={selectedInstance ? `Conectar — ${selectedInstance.name}` : 'Conectar instância'}
        description="O QR Code é buscado da Pastorini em tempo real e atualizado automaticamente."
        size="xl"
      >
        {selectedInstance ? (
          <QRCodeDisplay
            key={selectedInstance.id}
            instanceId={selectedInstance.id}
            initialStatus={selectedInstance.status}
            initialQRData={selectedInstance.qr_code_data}
            initialPublicQrUrl={selectedInstance.public_qr_url}
            initialRemoteInstanceId={selectedInstance.remote_instance_id}
            forcePrepareFlow={qrFlowMode === 'prepare'}
          />
        ) : (
          <div className="glass-card flex items-center gap-3 p-6 text-sm text-[var(--text-secondary)]">
            <Link2 size={18} className="text-[var(--color-brand)]" />
            Selecione uma instância para visualizar o fluxo de conexão.
          </div>
        )}
      </Modal>

      <Modal
        open={instanceToDelete !== null}
        onClose={() => !isDeleting && setInstanceToDelete(null)}
        title="Excluir instância"
        size="sm"
      >
        <div className="space-y-5">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[rgba(239,68,68,0.12)] text-[var(--color-error)]">
              <AlertTriangle size={20} />
            </div>
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">
                Tem certeza que deseja excluir{' '}
                <span className="font-semibold">"{instanceToDelete?.name}"</span>?
              </p>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                Esta ação removerá permanentemente a instância e encerrará a conexão com o WhatsApp. Ela não pode ser desfeita.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setInstanceToDelete(null)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirmed}
              loading={isDeleting}
            >
              Excluir instância
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={instanceWebhookDraft !== null}
        onClose={() => !isSavingWebhook && setInstanceWebhookDraft(null)}
        title={
          instanceWebhookDraft
            ? `Webhook da instância — ${instanceWebhookDraft.name}`
            : 'Webhook da instância'
        }
        description="Eventos enviados: message_received, message_sent e message_updated."
      >
        {instanceWebhookDraft ? (
          <div className="space-y-5">
            <Input
              label="URL do webhook"
              type="url"
              placeholder="https://seu-sistema.com/webhooks/whatsapp"
              value={instanceWebhookDraft.webhook_url}
              onChange={(event) =>
                setInstanceWebhookDraft((current) =>
                  current ? { ...current, webhook_url: event.target.value } : current,
                )
              }
              hint="Payload padronizado com conversa, contato e mensagem."
            />

            <label className="flex items-center justify-between gap-4 rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3">
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">Webhook ativo</p>
                <p className="text-xs text-[var(--text-muted)]">
                  Ative apenas quando o endpoint externo estiver pronto para receber os eventos.
                </p>
              </div>
              <input
                type="checkbox"
                checked={instanceWebhookDraft.webhook_enabled}
                onChange={(event) =>
                  setInstanceWebhookDraft((current) =>
                    current
                      ? { ...current, webhook_enabled: event.target.checked }
                      : current,
                  )
                }
              />
            </label>

            <div className="flex flex-wrap gap-2">
              {['message_received', 'message_sent', 'message_updated'].map((eventName) => (
                <span
                  key={eventName}
                  className="inline-flex min-h-[28px] items-center rounded-[14px] border border-[rgba(var(--color-brand-rgb),0.18)] bg-[rgba(var(--color-brand-rgb),0.12)] px-3 text-[11px] font-semibold text-[var(--color-brand)]"
                >
                  {eventName}
                </span>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setInstanceWebhookDraft(null)}
                disabled={isSavingWebhook}
              >
                Cancelar
              </Button>
              <Button onClick={() => handleSaveInstanceWebhook().catch(() => undefined)} loading={isSavingWebhook}>
                Salvar webhook
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
